import {
  CreateScheduledEventRequestDto,
  ScheduledEventFormSchema,
  ScheduledEventFormType,
  SuggestedScheduledEventDto,
  UpdateScheduledEventRequestDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { FormBuilder, IconLibrary } from '@act/storybook';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { autoSaveNotesRequests } from 'redux/requests/auto-save.requests';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import { createScheduledEventSuccess } from 'redux/slices/scheduledEventSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { scheduledEventDateTimeHelpers } from 'utilities/date-time-generator';
import { useToast } from '@act/react-components/app/ReusableToast';
import { RichTextEditorRef } from '@act/react-components/dialog-forms/rich-text-editor';
import { EventTypeExtended } from '@act/react-components/dialog-forms/scheduled-event-dialog-form-types';
import { GoalsSidePanel } from '@act/react-components/needs/need-detail-dialog/GoalsSidePanel';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { suggestedScheduledEventUpdated } from 'redux/redux';
import { TrackingProvider, useTracking } from '@act/react-components/tracking';

const ANYTIME = -1;
export const DATE_FORMAT = 'yyyy-MM-dd';

const defaultDate = DateTime.now().toFormat(DATE_FORMAT);

// Normalize note to HTML format for defaultValues (Zod transform only runs on validation)
const normalizeNoteForDefault = (note: string | undefined | null): string => {
  if (!note || note.trim() === '') {
    return '<p><br></p>';
  }
  // If already HTML, return as-is
  if (/<[^>]+>/.test(note)) {
    return note;
  }
  // Convert plain text to HTML format
  return `<p>${note}</p>`;
};

export interface SuggestedScheduleEventProps {
  user: UserDto;
  suggestedEvent?: SuggestedScheduledEventDto;
  open: boolean;
  onClose: (success?: boolean) => void;
}

export const SuggestedScheduleEvent = ({
  user,
  suggestedEvent,
  open,
  onClose,
}: SuggestedScheduleEventProps) => {
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { generateTrackingProps } = useTracking();

  // We currently have the llm sending the time as utc central time so we will convert it to local time for the user for now until we can have the llm send the correct time in local.
  const epochCentralToLocal = (epochMs: number): DateTime => {
    const utc = DateTime.fromMillis(epochMs, { zone: 'utc' });

    const central = utc.setZone('America/Chicago');

    return DateTime.local().set({
      hour: central.hour,
      minute: central.minute,
      second: 0,
      millisecond: 0,
    });
  };
  const suggestedEventTimeLocal = epochCentralToLocal(
    suggestedEvent?.time
  ).toMillis();

  const { control, handleSubmit, setValue, watch, formState, reset } =
    useForm<ScheduledEventFormType>({
      defaultValues: {
        eventType:
          (suggestedEvent?.type as
            | 'call'
            | 'message'
            | 'visit'
            | 'tbd'
            | 'reminder') || EventTypeExtended.CALL,
        date: suggestedEvent?.date || defaultDate,
        time:
          scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
            suggestedEventTimeLocal
          ) || ANYTIME,
        note: normalizeNoteForDefault(suggestedEvent?.note),
        visitLocationId:
          suggestedEvent?.visitLocationAddressId ||
          suggestedEvent?.eventType === 'visit'
            ? -1
            : null,
        otherLocation: suggestedEvent?.visitLocation || null,
      },
      mode: 'onChange',
      resolver: zodResolver(ScheduledEventFormSchema),
    });

  const addresses = user?.addresses;
  const visitLocationId = watch('visitLocationId');
  const eventType = watch('eventType');

  // Ref to access RichTextEditor methods
  const richTextEditorRef = useRef<RichTextEditorRef>(null);

  const [
    createdScheduledEvent,
    isCreatingScheduledEvent,
    createScheduledEvent,
  ] = useApiCall(scheduledEventRequests.createScheduledEventForUser);

  const [_deletedAutoSave, _isAutoSaveDeleting, deleteAutoSave] = useApiCall(
    autoSaveNotesRequests.deleteAutoSavedNote
  );

  const [autoSavedNote, _isLoadingGet, getAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.getAutoSavedNote
  );

  const [_autoSaveAction, _isLoadingSave, postAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.postAutoSavedNote
  );

  useEffect(() => {
    if (visitLocationId !== -1) setValue('otherLocation', null);
  }, [visitLocationId, setValue]);

  const addressOptions = useMemo(() => {
    let returnValue: Array<{ key: number; label: string; value: number }> = [];
    if (addresses?.length) {
      returnValue = addresses.map(address => {
        return {
          key: address?.id,
          label: `${address?.line1}, ${address?.line2 ?? ''}, ${
            address?.city
          }, ${address?.state} ${address?.zip}`,
          value: address?.id,
        };
      });
    }
    returnValue.push({ key: -1, label: 'Other', value: -1 });
    return returnValue;
  }, [addresses]);

  // Reset location fields when event type changes away from 'visit'
  useEffect(() => {
    if (eventType !== 'visit') {
      setValue('visitLocationId', null);
      setValue('otherLocation', null);
    } else if (eventType === 'visit' && !visitLocationId) {
      // When switching to visit, set default to first address if available
      const firstAddressId = addressOptions[0]?.value;
      if (firstAddressId !== undefined && firstAddressId !== -1) {
        setValue('visitLocationId', firstAddressId);
      }
    }
  }, [eventType, visitLocationId, addressOptions, setValue]);

  const timeOptions = useMemo(
    () => [
      { key: ANYTIME, label: 'Anytime', value: ANYTIME },
      ...scheduledEventDateTimeHelpers
        .getTimeOptions(
          suggestedEvent?.time
            ? scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
                suggestedEvent.time
              )
            : undefined
        )
        .map(option => ({
          key: option.value,
          label: option.label,
          value: option.value,
        })),
    ],
    [suggestedEvent]
  );

  // Load auto-saved note on mount (only if no suggestedEvent note exists)
  useEffect(() => {
    // Only load autosave if there's no suggested event note
    if (suggestedEvent?.note) return;

    async function getAutoSaved() {
      try {
        await getAutoSavedNote({
          params: { subjectId: user?.id, type: 'events' },
        });
      } catch (err) {
        console.error(`Error getting autosaved note: ${err}`);
      }
    }

    void getAutoSaved();
  }, []);

  useEffect(() => {
    // Only set autosaved note if there's no suggested event note
    if (!autoSavedNote || suggestedEvent?.note) return;
    // Normalize empty strings to match RichTextEditor's expected format
    const noteValue = autoSavedNote.text?.trim() || '<p><br></p>';
    setValue('note', noteValue);
  }, [autoSavedNote, suggestedEvent?.note, setValue]);

  async function handleAutoSave(autoSaveNote: string) {
    postAutoSavedNote({
      params: { subjectId: user?.id, type: 'events' },
      body: { text: autoSaveNote },
    });
  }

  async function handleSave(data: ScheduledEventFormType) {
    // Cancel any pending autosave before proceeding with manual save
    if (richTextEditorRef.current) {
      richTextEditorRef.current.cancelPendingAutoSave();
    }

    // If we cancelled an autosave, the form data might not be up to date
    // Get the current value from the RichTextEditor to ensure we have the latest
    let currentNoteValue: string | undefined;
    if (richTextEditorRef.current) {
      currentNoteValue = richTextEditorRef.current.getCurrentValue();
      data.note = currentNoteValue;
    }

    const dateValue = (data.date as string) || defaultDate;
    const body:
      | CreateScheduledEventRequestDto
      | UpdateScheduledEventRequestDto = {
      date: dateValue,
      type: data.eventType,
      visitLocationAddressId:
        data.visitLocationId !== -1 ? data.visitLocationId : null,
      visitLocation: data.visitLocationId === -1 ? data.otherLocation : null,
      ...(!!data.note && { note: data.note }),
    };

    // Time is required by Zod validation
    if (data.time === ANYTIME) {
      body.time = null;
    } else {
      const date = DateTime.fromFormat(dateValue, DATE_FORMAT);
      const time = date.plus({ milliseconds: data.time }).toMillis();
      body.time = time;
    }
    if (suggestedEvent?.id) {
      body.suggestedScheduledEventId = suggestedEvent.id;
    }

    try {
      createScheduledEvent({
        params: { userId: user.id },
        body,
      });
      if (richTextEditorRef.current && currentNoteValue) {
        richTextEditorRef.current.markAsSaved(currentNoteValue);
      }
    } catch (err) {
      console.error(`Error saving scheduled event: ${err}`);
    }
  }

  useEffect(() => {
    if (createdScheduledEvent) {
      if (suggestedEvent) {
        dispatch(suggestedScheduledEventUpdated(suggestedEvent));
      }
      dispatch(createScheduledEventSuccess(createdScheduledEvent));
      deleteAutoSave({
        params: { subjectId: user?.id, type: 'events' },
      });
      toast('Scheduled event has been created successfully');
      onClose(true);
    }
  }, [
    createdScheduledEvent,
    dispatch,
    user?.id,
    onClose,
    suggestedEvent,
    toast,
    deleteAutoSave,
  ]);

  const formConfig: FormConfig<ScheduledEventFormType> = useMemo(() => {
    const config: FormConfig<ScheduledEventFormType> = [
      {
        type: 'label',
        label: 'Select an event type.',
      },
      {
        type: 'input',
        name: 'eventType',
        label: 'Event Type',
        input: 'radioGroup',
        props: {
          options: [
            {
              label: 'Phone call',
              value: 'call',
            },
            {
              label: 'Text message',
              value: 'message',
            },
            {
              label: 'Home visit',
              value: 'visit',
            },
          ],
          row: true,
        },
      },
    ];

    // Add visit location fields only if event type is 'visit'
    if (eventType === 'visit') {
      config.push({
        type: 'input',
        name: 'visitLocationId',
        label: 'Location',
        input: 'select',
        props: {
          options: addressOptions,
          fullWidth: true,
        },
      });

      if (visitLocationId === -1) {
        config.push({
          type: 'input',
          name: 'otherLocation',
          label: 'Location',
          input: 'text',
        });
      }
    }
    config.push(
      {
        type: 'label',
        label: 'Select a time that works best.',
      },
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'date',
            label: 'Date',
            input: 'date',
            props: {
              minDate: DateTime.now(),
              required: true,
              inputType: 'date',
            },
          },
          {
            type: 'input',
            name: 'time',
            label: 'Start time',
            input: 'select',
            props: {
              options: timeOptions,
              fullWidth: true,
            },
          },
        ],
      }
    );
    config.push(
      {
        type: 'label',
        label: 'Leave a note to help you remember what to talk about.',
      },
      {
        type: 'input',
        name: 'note',
        label: 'Note',
        input: 'richText',
        props: {
          autoSaveFn: handleAutoSave,
          size: 'small',
          ref: richTextEditorRef,
          defaultValue: formState.defaultValues?.note ?? '<p><br></p>',
        },
      }
    );

    return config;
  }, [
    eventType,
    visitLocationId,
    addressOptions,
    timeOptions,
    handleAutoSave,
    formState.defaultValues?.note,
  ]);

  const isSaving = isCreatingScheduledEvent;
  const isSubmitting = isSaving || formState.isSubmitting;
  const { isDirty } = formState;

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  return (
    <TrackingProvider contextName="add-llm-suggested-scheduled-event-post-call">
      <GoalsSidePanel
        titleContent={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
            }}
          >
            <IconButton onClick={handleClose}>
              <IconLibrary iconName="ArrowLeft" size={32} weight="regular" />
            </IconButton>
            <Typography
              variant="titleLarge"
              fontWeight={500}
              sx={{ alignSelf: 'center' }}
            >
              {'Schedule Event'} for {user.firstName} {user.lastName}
            </Typography>
          </Box>
        }
        content={
          <Box sx={{ padding: '16px 0' }}>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
              disabled={isSubmitting}
            />
          </Box>
        }
        footerContent={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              {...generateTrackingProps('submit-button')}
              variant="contained"
              onClick={handleSubmit(data => {
                void handleSave(data);
              })}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Saving...' : 'Schedule event'}
            </Button>
          </Box>
        }
        open={open}
      />
    </TrackingProvider>
  );
};
